import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import './App.css';

/* ═══════════════════════════════════════════════ HELPERS ══ */
const generateCode = () => {
  const c = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const p = (n) => Array.from({ length: n }, () => c[Math.floor(Math.random() * c.length)]).join('');
  return `WTC-${p(4)}-${p(4)}`;
};

const useReveal = () => {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.09 }
    );
    document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

/* ═══════════════════════════════════════════════ NAVBAR ══ */
function Navbar() {
  const [solid, setSolid] = useState(false);
  const [open,  setOpen]  = useState(false);
  useEffect(() => {
    const h = () => setSolid(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);
  const links = [
    { href: '#how',       label: 'How It Works' },
    { href: '#gallery',   label: 'NFT Gallery'  },
    { href: '#presale',   label: 'Presale'       },
    { href: '#roadmap',   label: 'Roadmap'       },
    { href: '#community', label: 'Community'     },
  ];
  const scroll = (e, href) => {
    e.preventDefault();
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <nav className={`navbar${solid ? ' solid' : ''}`}>
      <a href="/" className="nav-logo" onClick={(e) => scroll(e, '#hero')}>
        <img src="/wtchtube-logo.png" alt="WTCHTUBE" className="nav-logo-img" onError={(e) => { e.target.style.display='none'; }}/>
        <span className="nav-logo-text">WTCHTUBE</span>
      </a>
      <ul className="nav-links">
        {links.map((l) => <li key={l.href}><a href={l.href} onClick={(e) => scroll(e, l.href)}>{l.label}</a></li>)}
      </ul>
      <a href="#presale" className="nav-cta" onClick={(e) => scroll(e, '#presale')}>Request Presale</a>
      <button className="nav-burger" onClick={() => setOpen(!open)} aria-label="Menu">
        <span/><span/><span/>
      </button>
      {open && (
        <div className="nav-mobile">
          {links.map((l) => <a key={l.href} href={l.href} onClick={(e) => scroll(e, l.href)}>{l.label}</a>)}
          <a href="#presale" className="nav-cta-mobile" onClick={(e) => scroll(e, '#presale')}>Request Presale</a>
        </div>
      )}
    </nav>
  );
}

/* ═══════════════════════════════════════════════ HERO ══ */
function Hero() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, nodes = [], raf;
    const NUM = 80, CONN = 130;
    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);
    for (let i = 0; i < NUM; i++) nodes.push({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 1.5 + 0.5, pulse: Math.random() * Math.PI * 2,
    });
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      nodes.forEach((n) => {
        n.x += n.vx; n.y += n.vy; n.pulse += 0.018;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
      });
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONN) {
            ctx.strokeStyle = `rgba(139,92,246,${(1 - d / CONN) * 0.3})`;
            ctx.lineWidth = 0.7;
            ctx.beginPath(); ctx.moveTo(nodes[i].x, nodes[i].y); ctx.lineTo(nodes[j].x, nodes[j].y); ctx.stroke();
          }
        }
      }
      nodes.forEach((n) => {
        const g = 0.5 + Math.sin(n.pulse) * 0.4;
        const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r * 4);
        grad.addColorStop(0, `rgba(167,139,250,${g})`);
        grad.addColorStop(1, 'rgba(139,92,246,0)');
        ctx.fillStyle = grad;
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r * 4, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = `rgba(255,255,255,${g * 0.9})`;
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2); ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);

  const scroll = (e, href) => { e.preventDefault(); document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' }); };

  return (
    <section id="hero" className="hero-section">
      <canvas ref={canvasRef} className="hero-canvas" />
      <div className="hero-orb hero-orb-1" /><div className="hero-orb hero-orb-2" /><div className="hero-orb hero-orb-3" />
      <div className="hero-content">
        <div className="hero-eyebrow"><span className="eye-dot" />&nbsp;NFT Presale — Coming Soon · Register Now</div>
        <div className="hero-logo-wrap">
          <img src="/wtchtube-logo.png" alt="WTCHTUBE" onError={(e) => { e.target.style.display='none'; }} />
        </div>
        <h1 className="hero-h1">WTCHTUBE<br /><span>Watch. Earn.</span></h1>
        <p className="hero-sub">
          The world's first <strong>Watch-to-Earn</strong> app. Hold a <strong>WtchTube Genesis NFT</strong>,
          watch curated videos, and earn <strong>$WTCH tokens</strong> every single day.
          Your attention has never been more valuable.
        </p>
        <div className="hero-btns">
          <a href="#presale" className="btn-primary" onClick={(e) => scroll(e, '#presale')}>🎟️ Request Presale Access</a>
          <a href="#gallery" className="btn-ghost"   onClick={(e) => scroll(e, '#gallery')}>💎 View NFT Collection</a>
        </div>
        <div className="hero-scroll-hint"><div className="scroll-line" /><span>Scroll to explore</span></div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ TICKER ══ */
function Ticker() {
  const items = [
    ['WTCHTUBE','Platform'],['$WTCH','Native Token'],['+10 $WTCH','Per Video'],
    ['5 / day','Daily Limit'],['Genesis NFT','Current NFT'],['Common','NFT Rarity'],
    ['Android','Available Now'],['Presale','Coming Soon'],
  ];
  return (
    <div className="ticker-wrap">
      <div className="ticker-track">
        {[...items, ...items].map(([v, l], i) => (
          <div key={i} className="ticker-item">
            <span className="t-val">{v}</span>
            <span className="t-lbl">{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════ HOW IT WORKS ══ */
function HowItWorks() {
  return (
    <section id="how" className="section bg-2">
      <div className="container">
        <div className="reveal">
          <div className="section-tag">The Process</div>
          <h2 className="section-h">THREE STEPS TO<br />DAILY EARNINGS</h2>
          <p className="section-p">No trading bots. No complex DeFi. Watch, hold, collect — the simplest earn model ever built.</p>
        </div>
        <div className="how-grid">
          {[
            { n:'01', ico:'📲', title:'Download & Sign Up',        desc:'Install the free WTCHTUBE Android app. Create your account with just an email in under 60 seconds. Enter a referral code for bonus $WTCH on day one.', chip:'✦ 100% Free to start' },
            { n:'02', ico:'💎', title:'Hold a WtchTube Genesis NFT',  desc:'Your NFT is your personal earning engine. The Genesis Common NFT earns +10 $WTCH per video with 5 earning videos per day. More powerful tiers coming in the presale.', chip:'+10 $WTCH per video' },
            { n:'03', ico:'🎬', title:'Watch & Collect $WTCH',    desc:'Browse curated content, watch to completion, receive $WTCH instantly. Keep your NFT\'s durability at 100% by repairing it to maintain maximum earning power.', chip:'Daily $WTCH payouts' },
          ].map((s) => (
            <div key={s.n} className="how-card reveal">
              <div className="how-num">{s.n}</div>
              <span className="how-ico">{s.ico}</span>
              <div className="how-title">{s.title}</div>
              <div className="how-desc">{s.desc}</div>
              <div className="how-chip">{s.chip}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ NFT GALLERY ══ */
const GALLERY_TIERS = [
  {
    key:'common',     label:'Common',     color:'#60a5fa', glow:'rgba(96,165,250,0.28)',   dir:'left',  speed:65,
    exts:['.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.png','.png','.png','.png','.png','.png','.png','.png','.png','.png','.png'],
  },
  {
    key:'uncommon',   label:'Uncommon',   color:'#34d399', glow:'rgba(52,211,153,0.28)',   dir:'right', speed:46,
    exts:['.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.png','.png','.png','.png'],
  },
  {
    key:'rare',       label:'Rare',       color:'#c084fc', glow:'rgba(192,132,252,0.32)',  dir:'left',  speed:27,
    exts:['.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg'],
  },
  {
    key:'ultra-rare', label:'Ultra Rare', color:'#fb923c', glow:'rgba(251,146,60,0.32)',   dir:'right', speed:32,
    exts:['.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg'],
  },
];

function NftGallery() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="gallery" className="section bg-1 gallery-section">

      {/* ── Lightbox ── */}
      {lightbox && (
        <div className="lb-overlay" onClick={() => setLightbox(null)}>
          <div className="lb-box" onClick={e => e.stopPropagation()}>
            <button className="lb-close" onClick={() => setLightbox(null)}>✕</button>
            <img src={lightbox.img} alt={lightbox.name} className="lb-img" />
            <div className="lb-meta">
              <span className="lb-name">{lightbox.name}</span>
              <span className="lb-tier" style={{ color: lightbox.color }}>{lightbox.label}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="container">
        <div className="reveal centered">
          <div className="section-tag">The Collection</div>
          <h2 className="section-h">WTCHTUBE GENESIS<br />NFT COLLECTION</h2>
          <p className="section-p">
            63 unique NFTs across 4 rarity tiers — each one earns $WTCH every time you watch.
            Hold rarer NFTs to unlock higher earning rates and daily bonuses.
          </p>
        </div>
      </div>

      {/* ── Marquee Rows ── */}
      <div className="mq-rows">
        {GALLERY_TIERS.map((tier) => {
          const nfts = Array.from({ length: tier.exts.length }, (_, i) => ({
            img:   `/nfts/${tier.key}/${i}${tier.exts[i]}`,
            name:  `Genesis #${String(i).padStart(2, '0')}`,
            label: tier.label,
            color: tier.color,
          }));
          // Duplicate for seamless loop
          const items = [...nfts, ...nfts];

          return (
            <div key={tier.key} className="mq-row reveal">
              {/* Row header strip */}
              <div className="mq-row-header" style={{ '--rc': tier.color, '--rg': tier.glow }}>
                <div className="mq-row-left">
                  <span className="mq-dot" style={{ background: tier.color, boxShadow: `0 0 10px ${tier.glow}` }} />
                  <span className="mq-tier-name" style={{ color: tier.color }}>{tier.label}</span>
                  
                </div>
                <div className="mq-dir-arrow" style={{ color: tier.color }}>
                  {tier.dir === 'left'
                    ? <span className="mq-arrow-track">⟶ ⟶ ⟶ ⟶ ⟶</span>
                    : <span className="mq-arrow-track mq-arrow-rtl">⟵ ⟵ ⟵ ⟵ ⟵</span>
                  }
                </div>
              </div>

              {/* Marquee track */}
              <div className="mq-track-outer">
                {/* Edge fade masks */}
                <div className="mq-fade-left"  style={{ '--fc': tier.glow }} />
                <div className="mq-fade-right" style={{ '--fc': tier.glow }} />

                <div
                  className={`mq-track mq-${tier.dir}`}
                  style={{ animationDuration: `${tier.speed}s` }}
                >
                  {items.map((n, i) => (
                    <div
                      key={i}
                      className="mq-card"
                      style={{ '--cc': tier.color, '--cg': tier.glow }}
                      onClick={() => setLightbox(n)}
                    >
                      <div className="mq-img-wrap">
                        <img
                          src={n.img}
                          alt={n.name}
                          loading="lazy"
                          onError={e => { e.target.style.visibility = 'hidden'; }}
                        />
                        {/* Hover overlay */}
                        <div className="mq-hover-overlay">
                          <div className="mq-hover-icon">⤢</div>
                        </div>
                      </div>
                      <div className="mq-card-foot">
                        <div className="mq-card-name">{n.name}</div>
                        <div className="mq-rarity-pill" style={{ color: tier.color, borderColor: tier.color }}>
                          {tier.label}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Summary strip ── */}
      <div className="container">
        <div className="gallery-summary reveal">
          {GALLERY_TIERS.map(t => (
            <div key={t.key} className="gs-item">
              <div className="gs-dot" style={{ background: t.color, boxShadow: `0 0 10px ${t.glow}` }} />
              <span className="gs-label" style={{ color: t.color }}>{t.label}</span>
              
            </div>
          ))}
          
        </div>
      </div>

    </section>
  );
}

/* ═══════════════════════════════════════════════ EARNING ══ */
function EarningSection() {
  const stats = [
    { ico:'🎥', title:'Per-Video Earning',  desc:'Every video watched to completion pays +10 $WTCH with a Common Genesis NFT. Rarer tiers earn more per video.',             val:'+10 $WTCH' },
    { ico:'📅', title:'Daily Video Cap',    desc:'5 earning videos per day with a Common Genesis NFT. Higher rarity NFTs unlock a larger daily earning window.',              val:'5/day'      },
    { ico:'🔧', title:'NFT Durability',     desc:'Watching videos gradually reduces durability. Repair your NFT using $WTCH to restore to 100% and keep earnings maxed.',    val:'100% max'   },
    { ico:'👥', title:'Referral Bonus',     desc:'Share your unique code. Both you and your friend earn bonus $WTCH on signup. No limit on how many people you can refer.',   val:'+Bonus'     },
  ];
  return (
    <section id="earn" className="section bg-2">
      <div className="container">
        <div className="reveal">
          <div className="section-tag">Earn Engine</div>
          <h2 className="section-h">HOW YOUR NFT<br />EARNS FOR YOU</h2>
        </div>
        <div className="earn-grid">
          <div className="earn-stats">
            {stats.map((s, i) => (
              <div key={s.title} className={`earn-stat reveal d${i + 1}`}>
                <div className="es-icon">{s.ico}</div>
                <div className="es-body"><div className="es-title">{s.title}</div><div className="es-desc">{s.desc}</div></div>
                <div className="es-val">{s.val}</div>
              </div>
            ))}
          </div>
          <div className="earn-visual reveal d2">
            <div className="ev-nft-img">
              <img src="/nfts/common/0.jpg" alt="WtchTube Genesis NFT" onError={(e) => { e.target.style.background='linear-gradient(135deg,#2d0a52,#4c1d95)'; e.target.style.visibility='hidden'; }} />
            </div>
            <div className="ev-nft-label">WTCHTUBE GENESIS · COMMON · LV.1</div>
            <div className="ev-steps">
              {[
                ['Watch video to completion →', '+10 $WTCH instantly credited'],
                ['Repeat up to', '5 times each day'],
                ['Durability drops →', 'repair to stay at 100%'],
                ['Upgrade NFT rarity →', 'multiply your per-video rate'],
                ['Refer friends →', 'earn bonus $WTCH for each'],
              ].map(([a, b], i) => (
                <div key={i} className="ev-step"><div className="ev-dot" /><div className="ev-step-text">{a} <strong>{b}</strong></div></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════ PRESALE FORM ══ */
function PresaleForm({ onSuccess }) {
  const [step,    setStep]    = useState('form'); // 'form' | 'submitting' | 'done'
  const [errors,  setErrors]  = useState({});
  const [copied,  setCopied]  = useState(false);
  const [code,    setCode]    = useState('');
  const [form,    setForm]    = useState({
    email: '', type: '', socialLink: '', spots: '1', handle: '',
  });

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email address.';
    if (!form.type) e.type = 'Please select Individual or Community.';
    if (!form.spots || isNaN(+form.spots) || +form.spots < 1) e.spots = 'Enter a valid number of spots (min 1).';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setStep('submitting');
    const accessCode = generateCode();
    const { error } = await supabase.from('presale_requests').insert({
      access_code:      accessCode,
      email:            form.email.trim().toLowerCase(),
      applicant_type:   form.type,
      social_link:      form.socialLink.trim() || null,
      discord_tg_x:     form.handle.trim() || null,
      spots_requested:  parseInt(form.spots, 10),
    });
    if (error) {
      setStep('form');
      setErrors({ submit: error.message });
      return;
    }
    setCode(accessCode);
    setStep('done');
  };

  const copy = () => {
    navigator.clipboard.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const reset = () => {
    setForm({ email:'', type:'', socialLink:'', spots:'1', handle:'' });
    setErrors({});
    setStep('form');
    setCode('');
    setCopied(false);
  };

  /* ── DONE STATE ── */
  if (step === 'done') {
    return (
      <div className="pf-success">
        <div className="pf-success-icon">✅</div>
        <h3 className="pf-success-title">Form Submitted!</h3>
        <p className="pf-success-sub">Your presale request has been received. Save the code below to check your application status.</p>
        <div className="pf-code-box">
          <div className="pf-code-label">⚠️ YOUR ACCESS CODE — SAVE THIS NOW</div>
          <div className="pf-code-value">{code}</div>
          <button className={`pf-copy-btn${copied ? ' copied' : ''}`} onClick={copy}>
            {copied ? '✓ Copied!' : '📋 Copy Code'}
          </button>
        </div>
        <div className="pf-code-instructions">
          <strong>How to use your code:</strong>
          <ol>
            <li>Save this code somewhere safe (notes app, screenshot, etc.)</li>
            <li>Scroll down to <em>"Check Application Status"</em> on this page</li>
            <li>Enter your code to see if you're approved, pending, or rejected</li>
            <li>Once approved, you'll be contacted at <strong>{form.email}</strong> with next steps</li>
          </ol>
        </div>
        <div className="pf-success-actions">
          <button className="pf-check-btn" onClick={() => document.getElementById('status-check')?.scrollIntoView({ behavior:'smooth' })}>
            🔍 Check My Status
          </button>
          <button className="pf-another-btn" onClick={reset}>
            📝 Submit Another Form
          </button>
        </div>
      </div>
    );
  }

  /* ── FORM STATE ── */
  return (
    <div className="pf-form">
      {/* Progress indicator */}
      <div className="pf-progress">
        <div className="pf-progress-bar" style={{ width: step === 'submitting' ? '100%' : '0%' }} />
      </div>

      <div className="pf-field">
        <label className="pf-label">Email Address <span className="req">*</span></label>
        <input
          className={`pf-input${errors.email ? ' error' : ''}`}
          type="email" value={form.email}
          onChange={(e) => set('email', e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
        />
        {errors.email && <div className="pf-err">{errors.email}</div>}
      </div>

      <div className="pf-field">
        <label className="pf-label">You are joining as <span className="req">*</span></label>
        <div className="pf-type-row">
          {[['individual','👤 Individual'],['community','🏢 Community']].map(([v,l]) => (
            <button
              key={v} type="button"
              className={`pf-type-btn${form.type === v ? ' selected' : ''}`}
              onClick={() => set('type', v)}
            >
              {l}
            </button>
          ))}
        </div>
        {errors.type && <div className="pf-err">{errors.type}</div>}
      </div>

      <div className="pf-field">
        <label className="pf-label">Social Profile / Community Link</label>
        <input
          className="pf-input"
          type="url" value={form.socialLink}
          onChange={(e) => set('socialLink', e.target.value)}
          placeholder="https://twitter.com/yourprofile or community URL"
        />
      </div>

      <div className="pf-field">
        <label className="pf-label">
          {form.type === 'community' ? 'How many spots can you fill?' : 'How many spots do you need?'} <span className="req">*</span>
        </label>
        <input
          className={`pf-input${errors.spots ? ' error' : ''}`}
          type="number" min="1" max="10000" value={form.spots}
          onChange={(e) => set('spots', e.target.value)}
          placeholder="e.g. 1"
        />
        {errors.spots && <div className="pf-err">{errors.spots}</div>}
      </div>

      <div className="pf-field">
        <label className="pf-label">Your Discord / Telegram / X Handle</label>
        <input
          className="pf-input"
          type="text" value={form.handle}
          onChange={(e) => set('handle', e.target.value)}
          placeholder="@yourhandle (any platform)"
        />
      </div>

      {errors.submit && <div className="pf-err pf-err-global">⚠️ {errors.submit}</div>}

      <button
        className={`pf-submit-btn${step === 'submitting' ? ' loading' : ''}`}
        onClick={submit}
        disabled={step === 'submitting'}
      >
        {step === 'submitting' ? (
          <><span className="pf-spinner" /> Submitting…</>
        ) : (
          'Secure My Spot on Whitelist →'
        )}
      </button>
      <div className="pf-note">No payment required now · You'll be notified when presale opens · Unsubscribe anytime</div>
    </div>
  );
}

/* ══════════════════════════════════════ STATUS CHECK ══ */
function StatusCheck() {
  const [code,    setCode]    = useState('');
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [err,     setErr]     = useState('');

  /* Auto-format as WTC-XXXX-XXXX while typing */
  const handleInput = (raw) => {
    // Strip everything that isn't alphanumeric
    const clean = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 11);
    let formatted = clean;
    if (clean.length > 3)  formatted = clean.slice(0,3) + '-' + clean.slice(3);
    if (clean.length > 7)  formatted = clean.slice(0,3) + '-' + clean.slice(3,7) + '-' + clean.slice(7);
    setCode(formatted);
    setErr('');
    setResult(null);
  };

  const check = async () => {
    if (!code.trim()) { setErr('Please enter your access code.'); return; }
    setErr(''); setLoading(true); setResult(null);
    const { data, error } = await supabase.rpc('check_presale_status', { p_code: code.trim().toUpperCase() });
    setLoading(false);
    if (error) { setErr(error.message); return; }
    if (!data || data.length === 0 || !data[0].found) {
      setResult({ found: false });
    } else {
      setResult(data[0]);
    }
  };

  const statusConfig = {
    pending:  { icon:'⏳', label:'Pending Review', color:'#fbbf24', bg:'rgba(251,191,36,0.1)',  border:'rgba(251,191,36,0.3)',  msg:'Your request is in the queue. We\'ll review it soon and notify you by email.' },
    approved: { icon:'✅', label:'Approved!',       color:'#4ade80', bg:'rgba(74,222,128,0.1)', border:'rgba(74,222,128,0.3)',  msg:'Congratulations! Check your email for presale access instructions.' },
    rejected: { icon:'❌', label:'Not Approved',    color:'#f87171', bg:'rgba(239,68,68,0.1)',  border:'rgba(239,68,68,0.3)',   msg:'' },
  };

  return (
    <div id="status-check" className="status-section">
      <div className="status-tag">Track Your Request</div>
      <h3 className="status-h">CHECK APPLICATION STATUS</h3>
      <p className="status-sub">Enter the access code you received after submitting your presale request.</p>

      <div className="status-input-row">
        <input
          className={`status-input${err ? ' error' : ''}`}
          value={code}
          onChange={(e) => handleInput(e.target.value)}
          placeholder="WTC-XXXX-XXXX"
          onKeyDown={(e) => e.key === 'Enter' && check()}
          maxLength={13}
          spellCheck={false}
          autoCapitalize="characters"
        />
        <button className="status-check-btn" onClick={check} disabled={loading}>
          {loading ? <span className="pf-spinner" /> : '🔍 Check'}
        </button>
      </div>
      {err && <div className="pf-err">{err}</div>}

      {result && !result.found && (
        <div className="status-card not-found">
          <span className="status-card-icon">🔎</span>
          <div className="status-card-title">Code Not Found</div>
          <div className="status-card-sub">No request matches <strong>{code}</strong>. Double-check for typos and try again.</div>
        </div>
      )}

      {result && result.found && (() => {
        const cfg = statusConfig[result.status] || statusConfig.pending;
        return (
          <div className="status-card found" style={{ background: cfg.bg, borderColor: cfg.border }}>
            <span className="status-card-icon">{cfg.icon}</span>
            <div className="status-card-title" style={{ color: cfg.color }}>{cfg.label}</div>
            <div className="status-meta-grid">
              <div><span className="smg-lbl">Type</span><span className="smg-val">{result.applicant_type === 'community' ? '🏢 Community' : '👤 Individual'}</span></div>
              <div><span className="smg-lbl">Spots</span><span className="smg-val">{result.spots_requested}</span></div>
              <div><span className="smg-lbl">Submitted</span><span className="smg-val">{new Date(result.submitted_at).toLocaleDateString()}</span></div>
            </div>
            {cfg.msg && <div className="status-msg">{cfg.msg}</div>}
            {result.status === 'rejected' && result.rejection_reason && (
              <div className="status-rejection">
                <strong>Reason:</strong> {result.rejection_reason}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/* ═══════════════════════════════════════ PRESALE SECTION ══ */
function PresaleNftSlider() {
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const commonExts = ['.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.jpg','.png','.png','.png','.png','.png','.png','.png','.png','.png','.png','.png'];
  const nfts = Array.from({ length: 24 }, (_, i) => ({
    img: `/nfts/common/${i}${commonExts[i]}`,
    name: `Genesis #${String(i).padStart(2,'0')}`,
  }));
  const total = nfts.length;

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setSlide(s => (s + 1) % total), 2600);
    return () => clearInterval(t);
  }, [paused, total]);

  const prev = () => { setPaused(true); setSlide(s => (s - 1 + total) % total); };
  const next = () => { setPaused(true); setSlide(s => (s + 1) % total); };

  const n = nfts[slide];
  return (
    <div className="presale-slider-wrap">
      <div className="presale-nft-glow" />
      <div className="presale-nft-card">
        <img
          key={n.img}
          src={n.img}
          alt={n.name}
          className="presale-slider-img"
          onError={(e) => { e.target.style.visibility = 'hidden'; }}
        />
        <div className="presale-nft-badge">✦ GENESIS — COMMON</div>
        <div className="presale-nft-lv">Lv.1</div>
        <div className="presale-nft-name-tag">{n.name}</div>
      </div>

      {/* Slider controls */}
      <div className="presale-slider-controls">
        <button className="ps-nav-btn" onClick={prev} aria-label="Previous NFT">‹</button>
        <div className="ps-dots">
          {nfts.map((_, i) => (
            <button
              key={i}
              className={`ps-dot${slide === i ? ' active' : ''}`}
              onClick={() => { setPaused(true); setSlide(i); }}
              aria-label={`NFT ${i + 1}`}
            />
          ))}
        </div>
        <button className="ps-nav-btn" onClick={next} aria-label="Next NFT">›</button>
      </div>

      {/* Genesis Collection Details */}
      <div className="genesis-details">
        <div className="genesis-title">✦ WTCH NFT Genesis Collection</div>
        <div className="genesis-grid">
          <div className="genesis-row">
            <span className="g-label">Presale Phase 1st Date</span>
            <span className="g-value g-soon">🔜 Soon</span>
          </div>
          <div className="genesis-row">
            <span className="g-label">Total Genesis Supply</span>
            <span className="g-value">3,333</span>
          </div>
          <div className="genesis-row">
            <span className="g-label">Presale Price</span>
            <span className="g-value g-tba">TBA</span>
          </div>
          <div className="genesis-row">
            <span className="g-label">Presale Chain</span>
            <span className="g-value g-tba">TBA</span>
          </div>
        </div>
        <div className="genesis-more">📡 More info coming soon — join the whitelist to be first to know</div>
      </div>
    </div>
  );
}

function PresaleSection() {
  return (
    <section id="presale" className="section bg-1 presale-section">
      <div className="container">
        <div className="presale-grid">
          {/* Left: NFT Slider + Genesis Details */}
          <div className="presale-nft-col reveal">
            <PresaleNftSlider />
          </div>

          {/* Right: Form */}
          <div className="presale-form-col reveal d2">
            <div className="presale-soon-badge">⏳ NFT Presale — Coming Soon</div>
            <h2 className="presale-h">BE FIRST.<br /><span>GET EARLY ACCESS.</span></h2>
            <p className="presale-desc">
              The WTCHTUBE NFT presale is <strong>not yet open</strong> — but we're building the whitelist right now.
              Registered users will receive <strong>priority access</strong>, lowest-ever pricing, OG holder status,
              and exclusive benefits unavailable at public launch.
            </p>
            <div className="presale-perks">
              {['💲 Lowest Price Ever','⚡ Instant Activation','🏆 OG Holder Badge','🎯 Priority Access'].map((p) => (
                <div key={p} className="presale-perk">{p}</div>
              ))}
            </div>
            <div className="presale-form-wrap">
              <div className="pf-header">🎟️ Request Presale Access</div>
              <div className="pf-sub">Join the whitelist — you'll be notified when presale opens</div>
              <PresaleForm />
            </div>
          </div>
        </div>

        {/* Status Check */}
        <StatusCheck />
      </div>
    </section>
  );
}

/* ════════════════════════════════════════ CALCULATOR ══ */
function Calculator() {
  const [expanded, setExpanded] = useState(false);
  const [vids, setVids] = useState(5);
  const rate = 10;
  const daily = rate * vids;
  return (
    <section id="calc" className="section bg-2">
      <div className="container">
        <div className="reveal centered">
          <div className="section-tag" style={{ justifyContent:'center' }}>Earnings Simulator</div>
          <h2 className="section-h">HOW MUCH CAN<br />YOU EARN?</h2>
          <p className="section-p">With the WtchTube Genesis NFT, see exactly how much $WTCH you can earn based on your daily activity.</p>
        </div>
        {!expanded ? (
          <div className="reveal centered" style={{ marginTop: 36 }}>
            <button className="btn-primary" onClick={() => setExpanded(true)} style={{ fontSize:'1rem', padding:'16px 42px', boxShadow:'0 0 48px rgba(124,58,237,.5)' }}>
              🧮 Calculate My Earnings
            </button>
            <p style={{ marginTop: 14, fontSize:'.76rem', color:'var(--m2)', fontFamily:'var(--fu)' }}>Interactive earnings calculator</p>
          </div>
        ) : (
          <div className="calc-box calc-appear">
            <div className="calc-field">
              <div className="calc-label">
                <span>Videos Watched Per Day</span>
                <em>{vids} video{vids > 1 ? 's' : ''}{vids === 5 ? ' (daily max)' : ''}</em>
              </div>
              <input type="range" min="1" max="5" value={vids} onChange={(e) => setVids(+e.target.value)} className="calc-range" />
              <div className="calc-range-note">Max 5 videos/day with Genesis NFT · Higher tiers unlock more in presale</div>
            </div>
            <div className="calc-result">
              <div className="calc-main">{daily.toLocaleString()}</div>
              <div className="calc-lbl">$WTCH earned today with Genesis NFT</div>
              <div className="calc-breakdown">
                {[['Weekly',(daily*7).toLocaleString()],['Monthly',(daily*30).toLocaleString()],['Yearly',(daily*365).toLocaleString()]].map(([l,v]) => (
                  <div key={l} className="calc-cell"><div className="calc-cell-val">{v}</div><div className="calc-cell-lbl">{l}</div></div>
                ))}
              </div>
            </div>
            <button onClick={() => setExpanded(false)} style={{ marginTop:16, background:'none', border:'1px solid rgba(255,255,255,.1)', borderRadius:8, color:'var(--m2)', fontFamily:'var(--fu)', fontSize:'.75rem', padding:'7px 18px', cursor:'pointer', display:'block', marginLeft:'auto' }}>
              ✕ Close calculator
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════ DOWNLOAD ══ */
function Download() {
  const [notified, setNotified] = useState(false);
  const [email, setEmail]       = useState('');
  return (
    <section id="download" className="section bg-1 download-section">
      <div className="container dl-content">
        <div className="reveal">
          <div className="dl-soon-badge">📲 App Download — Coming Soon</div>
          <h2 className="dl-h">GET THE APP<br /><span>FREE — ANDROID</span></h2>
          <p className="dl-sub">
            The WTCHTUBE app is currently in <strong>active development</strong> and will be available
            for public download soon. Enter your email to be notified the moment it goes live.
          </p>
          {!notified ? (
            <div className="dl-notify-form">
              <input
                className="dl-email-input"
                type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                onKeyDown={(e) => e.key === 'Enter' && email && setNotified(true)}
              />
              <button
                className="dl-notify-btn"
                onClick={() => { if (email) setNotified(true); }}
                disabled={!email}
              >
                Notify Me 🔔
              </button>
            </div>
          ) : (
            <div className="dl-notified">✅ You're on the list! We'll email <strong>{email}</strong> when the app is ready.</div>
          )}
          <div className="dl-meta">Android 8.0+ · Free to download · No crypto wallet needed to start</div>
        </div>
        <div className="phone-mock reveal d3">
          <div className="phone-logo">
            <img src="/wtchtube-logo.png" alt="logo" onError={(e) => { e.target.style.display='none'; }} />
          </div>
          <div className="phone-name">WTCHTUBE</div>
          <div className="phone-earn">Coming Soon</div>
          <div className="phone-bars">
            {[9,16,13,20,11].map((h, i) => <div key={i} className="phone-bar" style={{ height: h, animationDelay:`${i*0.15}s` }} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════ COMMUNITY ══ */
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="36" height="36" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const DiscordIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="38" height="38" aria-hidden="true">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="36" height="36" aria-hidden="true">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

function Community() {
  const socials = [
    { cls:'tw', Icon: XIcon,       name:'Twitter / X',   href:'https://x.com/WtchTube',               desc:'Real-time announcements, presale launch alerts, and NFT news. The fastest way to stay updated.',         cta:'Follow @WtchTube →' },
    { cls:'dc', Icon: DiscordIcon, name:'Discord Server', href:'https://discord.gg/vk9YMHqPkF',        desc:'The main hub for holders and earners. Get support, share earnings, and access exclusive channels.',     cta:'Join Discord →'     },
    { cls:'tg', Icon: TelegramIcon,name:'Telegram',       href:'https://t.me/+TDZVLNZsIy84Zjll',      desc:'Instant presale alerts and team announcements delivered directly to your phone.',                        cta:'Join Telegram →'    },
  ];
  return (
    <section id="community" className="section bg-2 centered">
      <div className="container">
        <div className="reveal">
          <div className="section-tag" style={{ justifyContent:'center' }}>Join The Community</div>
          <h2 className="section-h">STAY CONNECTED.<br />EARN TOGETHER.</h2>
          <p className="section-p">Get presale alerts, $WTCH earning tips, and NFT news. The best alpha goes to our community members first.</p>
        </div>
        <div className="soc-grid">
          {socials.map((s) => (
            <a key={s.name} href={s.href} target="_blank" rel="noreferrer" className={`soc-card ${s.cls} reveal`}>
              <span className="soc-icon"><s.Icon /></span>
              <div className="soc-name">{s.name}</div>
              <div className="soc-desc">{s.desc}</div>
              <div className="soc-cta">{s.cta}</div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════ ROADMAP ══ */
function Roadmap() {
  const [active, setActive] = useState(0);
  const phases = [
    {
      badge:'⚡ Phase I — Active Now', badgeCls:'badge-active',
      title:'Foundation & Launch',
      steps:[
        { done:true,  label:'Community Social Launch & Building'   },
        { done:true,  label:'Website Launch'                       },
        { done:false, label:'Android App Development', current:true },
        { done:false, label:'Launch App in Public BETA Testing'    },
        { done:false, label:'Launch Test WTCH Tokens (1:1) in App' },
      ],
    },
    {
      badge:'○ Phase II — Upcoming', badgeCls:'badge-soon',
      title:'Limited Presale & NFT Introduction',
      steps:[
        { label:'Limited User Phase I Presale WTCH Tickets Launch'     },
        { label:'Introduce NFT System in App for Presale Users'        },
        { label:'Introduce WTCH Earning Ways in App for Presale Users' },
        { label:'Introduce Play Area in Games'                         },
        { label:'Limited User Phase II Presale Launch'                 },
      ],
    },
    {
      badge:'○ Phase III — Upcoming', badgeCls:'badge-soon',
      title:'On-chain & Ecosystem Expansion',
      steps:[
        { label:'Limited User Phase III Presale Launch'                    },
        { label:'Launch Genesis Rest Supply'                               },
        { label:'Listing on Marketplace'                                   },
        { label:'Android App Conversion into On-chain'                     },
        { label:'Distribute $WTCH Tokens to Presale & Beta Testers (1:1)' },
        { label:'iOS App Development'                                      },
        { label:'NFT Rarity with Earning Introduce'                        },
      ],
    },
    {
      badge:'○ Phase IV — Future', badgeCls:'badge-soon',
      title:'Full Web3 Platform',
      steps:[
        { label:'$WTCH Token Listing'  },
        { label:'Swap & Wallet Connect'},
        { label:'NFT Marketplace in App'},
        { label:'iOS App Launch'        },
        { label:'Higher Rarity NFTs'    },
        { label:'DAO Governance'        },
        { label:'Play Area Expansion'   },
      ],
    },
  ];

  return (
    <section id="roadmap" className="section bg-1">
      <div className="container">
        <div className="reveal">
          <div className="section-tag">The Plan</div>
          <h2 className="section-h">ROADMAP</h2>
          <p className="section-p">Transparent milestones. We ship, we deliver, we expand — in that order.</p>
        </div>

        {/* Phase tab selector */}
        <div className="rm-tabs reveal">
          {phases.map((ph, i) => (
            <button
              key={i}
              className={`rm-tab${active === i ? ' active' : ''}${i === 0 ? ' rm-tab-active-phase' : ''}`}
              onClick={() => setActive(i)}
            >
              <span className="rm-tab-num">0{i + 1}</span>
              <span className="rm-tab-label">Phase {['I','II','III','IV'][i]}</span>
              {i === 0 && <span className="rm-tab-live">Live</span>}
            </button>
          ))}
        </div>

        {/* Sliding panel */}
        <div className="rm-slider-outer">
          <div
            className="rm-slider-track"
            style={{ transform:`translateX(-${active * 100}%)` }}
          >
            {phases.map((ph, pi) => (
              <div key={pi} className="rm-slide">
                <div className="rm-slide-inner">
                  {/* Header */}
                  <div className="rm-slide-hdr">
                    <div className={`rm-badge ${ph.badgeCls}`}>{ph.badge}</div>
                    <h3 className="rm-slide-title">{ph.title}</h3>
                    {pi === 0 && (
                      <div className="rm-current-indicator">
                        <span className="rm-current-dot" />
                        Currently at: <strong>Android App Development</strong>
                      </div>
                    )}
                  </div>
                  {/* Steps */}
                  <div className="rm-steps-grid">
                    {ph.steps.map((s, si) => (
                      <div key={si} className={`rm-step-card${s.done?' done':''}${s.current?' current':''}`}>
                        <div className="rm-step-icon">
                          {s.done    ? '✓' : s.current ? '⚡' : `${si + 1}`}
                        </div>
                        <div className="rm-step-text">{s.label}</div>
                        {s.current && <div className="rm-step-here">← We are here</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dot navigation */}
        <div className="rm-dots">
          {phases.map((_, i) => (
            <button key={i} className={`rm-dot${active === i ? ' active' : ''}`} onClick={() => setActive(i)} />
          ))}
        </div>

        {/* Prev / Next */}
        <div className="rm-nav">
          <button className="rm-nav-btn" onClick={() => setActive(a => Math.max(0, a-1))} disabled={active === 0}>‹ Previous</button>
          <span className="rm-nav-counter">Phase {['I','II','III','IV'][active]} of IV</span>
          <button className="rm-nav-btn" onClick={() => setActive(a => Math.min(phases.length-1, a+1))} disabled={active === phases.length-1}>Next ›</button>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════ FAQ ══ */
function Faq() {
  const [open,    setOpen]    = useState(null);
  const [visible, setVisible] = useState({});
  const listRef = useRef(null);

  /* Track each item's visibility in React state — NOT via external class mutation.
     This prevents React from stripping IntersectionObserver-added classes when
     it reconciles className after a state change (the FAQ disappearing bug). */
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) {
          const idx = parseInt(e.target.dataset.idx, 10);
          setVisible(v => ({ ...v, [idx]: true }));
          io.unobserve(e.target);
        }
      }),
      { threshold: 0.08 }
    );
    listRef.current?.querySelectorAll('[data-idx]').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  const items = [
    { q:'What NFTs currently exist in WTCHTUBE?',
      a:'Currently one NFT: the WtchTube Genesis NFT — Common rarity, Level 1. It earns +10 $WTCH per video with 5 videos per day. Higher rarity tiers (Uncommon, Rare, Epic, Legendary) are in development and will be released in upcoming presale rounds.' },
    { q:'When does the presale open?',
      a:'The presale launch date hasn\'t been announced yet. Register your email on this page to get a direct notification the moment it opens. Whitelist registrants get priority access and the best available pricing.' },
    { q:'Do I need crypto knowledge to start?',
      a:'Not at all. WTCHTUBE is built for everyone. Sign up with an email, download the app, get your NFT, and start earning. Your $WTCH balance lives inside the app. Wallet Connect for external withdrawals is a future feature.' },
    { q:'What happens when my NFT durability drops?',
      a:'Each video you watch reduces your NFT\'s durability slightly. You can repair it any time using $WTCH from your in-app balance to bring it back to 100%. Keeping durability high ensures earnings stay at their maximum rate.' },
    { q:'How does the referral system work?',
      a:'Every account gets a unique referral code. When someone signs up using your code, both you and your new referral receive bonus $WTCH tokens immediately. There\'s no limit on how many people you can refer.' },
    { q:'Is the app available on iOS?',
      a:'Currently WTCHTUBE is in development with Android as the first target platform. iOS development is planned for Phase III of the roadmap. Join our Discord or Telegram to be notified when iOS launches.' },
  ];

  return (
    <section id="faq" className="section bg-2">
      <div className="container">
        <div className="reveal centered">
          <div className="section-tag" style={{ justifyContent:'center' }}>Got Questions?</div>
          <h2 className="section-h">FAQ</h2>
        </div>
        <div className="faq-list" ref={listRef}>
          {items.map((item, i) => (
            <div
              key={i}
              data-idx={i}
              style={{ transitionDelay:`${i * 0.07}s` }}
              className={`faq-item faq-anim${visible[i] ? ' faq-visible' : ''}${open === i ? ' open' : ''}`}
              onClick={() => setOpen(open === i ? null : i)}
            >
              <div className="faq-q">
                <span>{item.q}</span>
                <div className={`faq-arrow${open === i ? ' faq-arrow-open' : ''}`}>▾</div>
              </div>
              <div className="faq-a">{item.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════ FOOTER ══ */
function Footer() {
  const scroll = (e, href) => { e.preventDefault(); document.querySelector(href)?.scrollIntoView({ behavior:'smooth' }); };
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div>
            <a href="/" className="footer-logo" onClick={(e) => scroll(e,'#hero')}>
              <img src="/wtchtube-logo.png" alt="WTCHTUBE" onError={(e) => { e.target.style.display='none'; }} />
              <span>WTCHTUBE</span>
            </a>
            <div className="footer-brand">Watch videos. Hold NFTs. Earn $WTCH every day. Your attention — finally working for you.</div>
          </div>
          <div>
            <div className="footer-col-title">Platform</div>
            <ul className="footer-links">
              {[['#how','How It Works'],['#gallery','NFT Collection'],['#presale','Presale Waitlist'],['#calc','Earn Calculator'],['#download','Download App']].map(([h,l]) => (
                <li key={h}><a href={h} onClick={(e) => scroll(e,h)}>{l}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="footer-col-title">Community</div>
            <ul className="footer-links">
              <li><a href="https://x.com/WtchTube" target="_blank" rel="noreferrer">Twitter / X</a></li>
              <li><a href="https://discord.gg/vk9YMHqPkF" target="_blank" rel="noreferrer">Discord</a></li>
              <li><a href="https://t.me/+TDZVLNZsIy84Zjll" target="_blank" rel="noreferrer">Telegram</a></li>
              <li><a href="#faq" onClick={(e) => scroll(e,'#faq')}>FAQ</a></li>
            </ul>
          </div>
          <div>
            <div className="footer-col-title">Legal</div>
            <ul className="footer-links">
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/terms">Terms &amp; Conditions</a></li>
              <li><a href="/disclaimer">Risk Disclaimer</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-copy">© 2026 WTCHTUBE. All rights reserved. $WTCH is a utility token, not a financial instrument.</div>
          <div className="footer-socials">
            <a href="https://x.com/WtchTube" target="_blank" rel="noreferrer" className="footer-soc footer-soc-x" title="Twitter / X">
              <XIcon />
            </a>
            <a href="https://discord.gg/vk9YMHqPkF" target="_blank" rel="noreferrer" className="footer-soc footer-soc-dc" title="Discord">
              <DiscordIcon />
            </a>
            <a href="https://t.me/+TDZVLNZsIy84Zjll" target="_blank" rel="noreferrer" className="footer-soc footer-soc-tg" title="Telegram">
              <TelegramIcon />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════ APP ══ */
export default function App() {
  useReveal();
  return (
    <>
      <Navbar />
      <Hero />
      <Ticker />
      <HowItWorks />
      <NftGallery />
      <EarningSection />
      <Roadmap />
      <PresaleSection />
      <Calculator />
      <Download />
      <Community />
      <Faq />
      <Footer />
    </>
  );
}
